import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { useComposerStore } from '../state/composerStore';
import { markDiscovered } from '../state/coachStore';

// Module-level debounce: prevents rapid double-taps from appending multiple quotes
let lastQuoteMs = 0;

interface ActiveSelection {
  text: string;
  rect: DOMRect;
  sourceEl: Element;
}

function extractQuoteText(text: string): string {
  const MAX = 500;
  const trimmed = text.trim();
  if (trimmed.length > MAX) {
    return trimmed.slice(0, MAX) + '… [selection truncated]';
  }
  return trimmed;
}

function formatAsBlockquote(text: string): string {
  return '> ' + text.split('\n').join('\n> ') + '\n\n';
}

/** Format a code block with language tag preserved */
function formatAsCodeBlockquote(text: string, lang: string): string {
  return '> ```' + lang + '\n> ' + text.split('\n').join('\n> ') + '\n> ```\n\n';
}

const BLOCK_SELECTORS = 'p, li, pre, blockquote, h1, h2, h3, h4, h5, h6, table, .code-block';

/** Show a brief "✓ Quoted" badge on the element to confirm capture */
function showQuoteBadge(el: Element) {
  const badge = document.createElement('span');
  badge.className = 'quote-success-badge';
  badge.textContent = '✓ Quoted';
  el.appendChild(badge);
  setTimeout(() => badge.remove(), 1800);
}

/** Perform the quote action — shared by floating pill and InputBar chip */
export function executeQuote(text: string, sourceEl: Element, sessionId: string | null) {
  // Debounce: ignore if a quote was already appended within the last 1.5s.
  const now = Date.now();
  if (now - lastQuoteMs < 1500) return;
  lastQuoteMs = now;

  const quoted = formatAsBlockquote(extractQuoteText(text));
  document.dispatchEvent(new CustomEvent('quote-source', { detail: { sourceEl } }));
  // Real usage of the double-tap-to-quote gesture (coach-tooltip-system.md §5.2) —
  // cancel the tip mid-display + mark seen. Fires for BOTH the dblclick fast path
  // and the QuotePill chip, since either is a legitimate "found it" signal.
  markDiscovered('double-tap-quote');
  // platform-isolation #1657 Phase 2b: append the quote to the session draft via the named
  // store action (was a `prefill-input` CustomEvent). Focus + FAB-open remain view-layer
  // events below (R-A2 permits view dispatches).
  if (sessionId) useComposerStore.getState().appendOrReplaceDraft(sessionId, quoted, 'append');
  window.getSelection()?.removeAllRanges();

  // Show visual feedback and open the compose panel for immediate editing
  showQuoteBadge(sourceEl.querySelector(BLOCK_SELECTORS) || sourceEl);
  document.dispatchEvent(new CustomEvent('focus-composer'));
  document.dispatchEvent(new CustomEvent('fab-request-open'));
}

/** Explain: open routing modal so user can choose current session, another session, or a new one with a model pick */
function executeExplain(text: string, sourceEl: Element) {
  document.dispatchEvent(new CustomEvent('explain-routing-request', { detail: { text: extractQuoteText(text), sourceEl } }));
  window.getSelection()?.removeAllRanges();
}

/** Build the prefill text for an explain action (shared with ExplainTargetModal via App.tsx) */
export function buildExplainText(text: string): string {
  return formatAsBlockquote(text) + 'Please explain this further.\n';
}

/** Share: open the unified share modal with the selected text */
function executeShare(text: string) {
  const snippet = extractQuoteText(text);
  document.dispatchEvent(new CustomEvent('share-text-snippet', { detail: snippet }));
  window.getSelection()?.removeAllRanges();
}

interface PillAction {
  label: string;
  ariaLabel: string;
  handler: (text: string, sourceEl: Element) => void;
}

const PILL_ACTIONS: PillAction[] = [
  { label: '🔍 Explain', ariaLabel: 'Quote and ask to explain', handler: executeExplain },
  { label: '↗ Share', ariaLabel: 'Share selected text', handler: (t, _el) => executeShare(t) },
];

/**
 * QuotePill — floating pill that appears below any text selection inside
 * an assistant message. Two actions: Explain (prefills compose with quote)
 * and Share (opens unified share modal with the selected snippet).
 *
 * Also dispatches `quote-selection-change` CustomEvent so the InputBar can
 * show its fallback chip simultaneously.
 */
export function QuotePill({ sessionId }: { sessionId: string | null }) {
  const [active, setActive] = useState<ActiveSelection | null>(null);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearActive = useCallback(() => {
    setActive(null);
    document.dispatchEvent(new CustomEvent('quote-selection-change', { detail: null }));
    if (dismissTimer.current) { clearTimeout(dismissTimer.current); dismissTimer.current = null; }
  }, []);

  // Track block-highlighted element for cleanup
  const highlightedBlockRef = useRef<Element | null>(null);

  const clearBlockHighlight = useCallback(() => {
    if (highlightedBlockRef.current) {
      highlightedBlockRef.current.classList.remove('quote-block-highlight');
      highlightedBlockRef.current = null;
    }
  }, []);

  // Double-tap/double-click paragraph fast path
  useEffect(() => {
    const onDblClick = (e: MouseEvent) => {
      // Debounce: ignore if a quote was already appended within the last 1.5s.
      // Without this, rapid taps fire multiple dblclick events → multiple quotes.
      const now = Date.now();
      if (now - lastQuoteMs < 1500) return;

      const target = e.target as Element;
      const mdContentEl = target.closest('.message.assistant .md-content');
      if (!mdContentEl) return;

      // Don't trigger while streaming
      if (mdContentEl.closest('[data-streaming="true"]')) return;

      // Find the closest block-level element
      const blockEl = target.closest(BLOCK_SELECTORS);
      if (!blockEl || !mdContentEl.contains(blockEl)) return;

      e.preventDefault();
      lastQuoteMs = now;

      // Clear any existing selection (dblclick selects a word by default)
      window.getSelection()?.removeAllRanges();
      // BottomComposer is always visible — no FAB to open. The block-highlight
      // pulse + the user's deliberate text selection are the only feedback paths.
    };

    document.addEventListener('dblclick', onDblClick);
    return () => document.removeEventListener('dblclick', onDblClick);
  }, [clearBlockHighlight]);

  useEffect(() => {
    const onSelectionChange = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || !sel.toString().trim()) {
        if (active) clearActive();
        return;
      }

      const range = sel.getRangeAt(0);
      const container = range.commonAncestorContainer;
      const node = container.nodeType === Node.ELEMENT_NODE
        ? (container as Element)
        : (container as Node).parentElement;

      const mdContentEl = node?.closest('.message.assistant .md-content');
      if (!mdContentEl) {
        if (active) clearActive();
        return;
      }

      // Don't show while message is still streaming
      if (mdContentEl.closest('[data-streaming="true"]')) {
        if (active) clearActive();
        return;
      }

      const rect = range.getBoundingClientRect();
      // Ignore zero-size rects (Firefox fires selectionchange before rect is ready)
      if (rect.width === 0 && rect.height === 0) return;

      const text = sel.toString();
      setActive({ text, rect, sourceEl: mdContentEl });
      document.dispatchEvent(new CustomEvent('quote-selection-change', { detail: { text, sourceEl: mdContentEl } }));

      // Auto-dismiss after 6 seconds of inactivity
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
      dismissTimer.current = setTimeout(() => clearActive(), 6000);
    };

    document.addEventListener('selectionchange', onSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', onSelectionChange);
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
    };
  }, [active, clearActive]);

  // Keyboard shortcut: Ctrl+Shift+Q / Cmd+Shift+Q
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'Q') {
        if (active) {
          e.preventDefault();
          executeQuote(active.text, active.sourceEl, sessionId);
          clearActive();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [active, clearActive]);

  if (!active) return null;

  const { text, rect, sourceEl } = active;

  // Position: below selection by default (avoids collision with native mobile toolbar
  // which appears at the top of selections on Android/iOS), flip above only when
  // there is not enough room below.
  const STRIP_W = 200; // narrower with 2 pills instead of 3
  const PILL_H = 44;
  const GAP = 8;

  let top = rect.bottom + GAP;
  if (top + PILL_H > (window.innerHeight || 800) - 8) top = rect.top - PILL_H - GAP; // flip above if not enough room below

  let left = rect.left + rect.width / 2 - STRIP_W / 2;
  left = Math.max(8, Math.min(left, (window.innerWidth || 400) - STRIP_W - 8));

  return (
    <div
      class="quote-pill-strip"
      style={{ position: 'fixed', top: `${top}px`, left: `${left}px` }}
    >
      {PILL_ACTIONS.map((action) => (
        <button
          key={action.label}
          class="quote-pill"
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            action.handler(text, sourceEl);
            clearActive();
          }}
          aria-label={action.ariaLabel}
        >
          {action.label}
        </button>
      ))}
    </div>
  );
}
